import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { Container, Row, Column } from '@pella/sash-framework';
import { Layout, SEO, Breadcrumbs, Image, CTA } from '../../components';
import { useRtaData } from '../../hooks/use-rta-data';
import { isValidArray } from '../../components/utilities/validations';
import ShopByGroup from '../../components/template/shop/shop-by-group';
import ScheduleConsultation from '../../components/page/rta/v2/home';
import CardRenderer from '../../components/common/card-types/card-renderer';
import CardSection from '../../components/common/card-section';
import LocalNavTop from '../../components/common/local-nav-top';

import styles from './index.module.scss';

const Shop = (props) => {
	const pageData = props.data.contentstackShop;
	const rtaData = useRtaData();

	return (
		<Layout
			id='template-Shop'
			pageTitle={pageData.seo?.title}
		>
			{pageData.local_nav?.nav_links?.[0] && (
				<LocalNavTop
					navTitle={pageData.local_nav.heading}
					linksArray={pageData.local_nav.nav_links.map((link) => link.cta[0])}
				/>
			)}

			{pageData.breadcrumbs && (
				<Breadcrumbs
					data={pageData.breadcrumbs}
					currentUrl={pageData.url}
				/>
			)}

			{pageData.hero && (
				<div className='shop-hero'>
					<div className='bg-black py-3'>
						<Container>
							{pageData.hero.heading && <h1 className='font-semibold text-white'>{pageData.hero.heading}</h1>}
							{pageData.hero.subheading && <p className='mt-1 text-white'>{pageData.hero.subheading}</p>}
						</Container>
					</div>

					{pageData.hero.image?.[0] && (
						<Image
							data={pageData.hero.image[0]}
							className={`${styles.heroImage} w-full`}
							loading='eager'
						/>
					)}
				</div>
			)}

			{isValidArray(pageData?.page_components) &&
				pageData.page_components.map((block, index) => {
					if (block.shop_by != null) {
						return (
							<ShopByGroup
								key={index}
								data={block.shop_by}
							/>
						);
					}

					if (block.callouts != null) {
						return (
							<div
								id={block.callouts.anchor_text}
								key={index}
								className='my-6'
							>
								<Container width={1024}>
									{isValidArray(block?.callouts.routes) &&
										block.callouts.routes.map((route, index) => (
											<div
												key={index}
												className={`${styles.callout} my-8 bg-white`}
											>
												{route.heading && <h3 className='m-0 bg-black px-4 py-2 font-normal text-white md:px-6'>{route.heading}</h3>}
												<div className='flex flex-wrap md:p-8'>
													{isValidArray(route?.image) && (
														<Image
															data={route.image[0]}
															className={styles.calloutImage}
														/>
													)}

													<div className='basis-full p-4 md:flex-1 md:py-0 md:pl-10 md:pr-0'>
														{route.description && (
															<div
																dangerouslySetInnerHTML={{ __html: route.description }}
																className='mb-3 md:mb-6'
															/>
														)}
														{isValidArray(route?.cta) &&
															route.cta.map((cta, index) => (
																<CTA
																	data={cta}
																	key={index}
																/>
															))}
													</div>
												</div>
											</div>
										))}
								</Container>
							</div>
						);
					}

					if (block.resources != null && block.resources.layout === 'heading-aside') {
						return (
							<div
								key={index}
								className='bg-neutral-50 py-4 md:py-8 xl:py-10'
							>
								<Container>
									<Row align='center'>
										<Column
											xs={12}
											xl={4}
										>
											{block.resources.heading && <h2 className='mb-4 text-lg font-semibold uppercase tracking-3px md:text-3xl xl:mb-0'>{block.resources.heading}</h2>}
										</Column>

										<Column
											xs={12}
											xl={8}
										>
											{isValidArray(block.resources.group?.cards) && (
												<Row verticalGutters>
													{block.resources.group.cards.map((card, index) => (
														<Column
															key={index}
															xs={12}
															md='fluid'
														>
															<CardRenderer data={card} />
														</Column>
													))}
												</Row>
											)}
										</Column>
									</Row>
								</Container>
							</div>
						);
					}

					if (block.rta != null) {
						return (
							<div
								key={index}
								className='my-5 md:my-8'
							>
								<Container>
									{block.rta.heading && <h2 className='mb-2 font-semibold'>{block.rta.heading}</h2>}
									{block.rta.subheading && <p className='mb-6'>{block.rta.subheading}</p>}
									{block.rta.description_heading && <p className='font-semibold'>{block.rta.description_heading}</p>}
									{block.rta.description && (
										<div
											className={styles.rtaFormDesc}
											dangerouslySetInnerHTML={{ __html: block.rta.description }}
										/>
									)}

									<div className={`${styles.rtaForm} mt-8 p-4 md:mt-10 md:p-6 xl:p-10`}>
										{block.rta.rta_heading && <p className='mb-6 md:text-xl xl:mb-10'>{block.rta.rta_heading}</p>}

										{typeof window !== 'undefined' && (
											<ScheduleConsultation
												data={rtaData}
												location={window.location}
												layout='modal'
												ctaText='See Availability'
											/>
										)}
									</div>
								</Container>
							</div>
						);
					}

					if (block.cards != null) {
						return (
							<CardSection
								data={block.cards}
								key={index}
								className={styles.shopCards}
							/>
						);
					}
				})}
		</Layout>
	);
};

Shop.propTypes = {
	data: PropTypes.object,
};

export default Shop;

// eslint-disable-next-line react/prop-types -- fix this
export const Head = ({ data }) => (
	<SEO
		// eslint-disable-next-line react/prop-types -- fix this
		{...data.contentstackShop.seo}
	/>
);

export const query = graphql`
	query ($uid: String!) {
		contentstackShop(id: { eq: $uid }) {
			url
			breadcrumbs {
				active_page
				back_pages {
					...CTA
				}
			}
			local_nav {
				...LocalNav
			}
			hero {
				heading
				subheading
				image {
					...Image
					image {
						url
						gatsbyImageData(quality: 90, layout: FULL_WIDTH)
					}
				}
			}
			page_components {
				shop_by {
					anchor_text
					heading
					shop_by
					group {
						body
						link {
							title
							href
						}
					}
					product {
						image {
							...Image
							image {
								url
								gatsbyImageData(quality: 90, height: 90)
							}
						}
						heading
						material
						flag_type
						group {
							image {
								...Image
								image {
									url
									gatsbyImageData(quality: 90)
								}
							}
							heading
							description
							feature
							cta {
								...CTA
							}
							tiles {
								...Tile
							}
						}
					}
				}
				callouts {
					anchor_text
					routes {
						image {
							...Image
							image {
								url
								gatsbyImageData(quality: 90)
							}
						}
						heading
						description
						cta {
							...CTA
						}
					}
				}
				rta {
					heading
					subheading
					description_heading
					description
					rta_heading
				}
				cards {
					...CardSection
				}
				image_aside {
					...ImageAside
				}
			}
			seo {
				title
				description
				noIndex: no_index_page
			}
		}
	}
`;
